import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Grid } from '@mui/material';
import { blobToDataURL, resizeImageIfNeeded } from '@nx-smartmonkey/shared/helpers';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import styled from 'styled-components';

import iconCloseCamera from '../../../../../../../assets/img/camera/icon-closeCamera.svg';
import iconGallery from '../../../../../../../assets/img/camera/icon-gallery.svg';
import iconTurnCamera from '../../../../../../../assets/img/camera/icon-turnImage.svg';
import iconUploadCamera from '../../../../../../../assets/img/camera/icon-uploadImage.svg';
import DeleteIcon from '../../../../../../../assets/svg/DeleteIcon';
import { ImageUploadedProps, MimeTypes } from '../../../../../../../domain/report/StopReportCreate';
import { intl } from '../../../../../../../helpers/IntGlobalProvider';
import { Text } from '../../../../../../../react-components/Text';

const MaskedOverflow = styled.div`
  /* scroll bar width, for use in mask calculations */
  --scrollbar-width: 8px;

  /* mask fade distance, for use in mask calculations */
  --mask-width: 12px;

  /* If content exceeds width of container, overflow! */
  overflow-x: auto;

  /* Our width limit */
  width: 100%;

  /* Need to make sure container has right space,
  otherwise content at the right is always faded out */
  padding-right: var(--mask-width);

  /* Keep some space between content and scrollbar */
  padding-bottom: 2px;

  /* The CSS mask */

  /* The content mask is a linear gradient from left to right */
  --mask-image-content: linear-gradient(
    to right,
    transparent,
    black var(--mask-width),
    black calc(100% - var(--mask-width)),
    transparent
  );

  /* Here we scale the content gradient to the height of the container 
  minus the scrollbar width. The width is the full container width */
  --mask-size-content: 100% calc(100% - var(--scrollbar-width));

  /* The scrollbar mask is a black pixel */
  --mask-image-scrollbar: linear-gradient(black, black);

  /* The height of our black pixel is the height of the scrollbar.
  The width is the full container width */
  --mask-size-scrollbar: 100% var(--scrollbar-width);

  /* Apply the mask image and mask size variables */
  mask-image: var(--mask-image-content), var(--mask-image-scrollbar);
  mask-size: var(--mask-size-content), var(--mask-size-scrollbar);

  /* Position the content gradient in the top left, and the 
  scroll gradient in the bottom left */
  mask-position: 0 0, 0 100%;

  /* We don't repeat our mask images */
  mask-repeat: no-repeat, no-repeat;

  /* make it a row*/
  display: flex;
  flex-direction: row;

  padding: 0px 10px;
`;

type ServiceImageCameraMenuProps = {
  imageData: Blob;
  style?: React.CSSProperties;
  onClick?: () => void;
};

const generateRandomId = (size: number) =>
  [...Array(size)].map(() => Math.floor(Math.random() * 16).toString(16)).join(``);

/**
 * Renders an image using a blob.
 *
 * @param imageData - The image data to be displayed.
 * @param style - The style object to be applied to the image.
 * @param onClick - The click event handler for the image.
 * @returns The rendered camera menu component.
 */
export const ServiceImageCameraMenu = ({ imageData, style, onClick }: ServiceImageCameraMenuProps) => {
  const [imageUrl, setImageUrl] = useState<string | null | undefined>(undefined);

  /**
   * Retrieves the image URL from the provided image blob.
   *
   * @param imageBlob - The image blob to retrieve the URL from.
   */
  const getImage = useCallback((imageBlob: Blob) => {
    (async () => {
      const temp = await imageBlob.text();
      setImageUrl(temp);
    })();
  }, []);

  useEffect(() => {
    if (imageData) {
      return getImage(imageData);
    }
    return setImageUrl(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageData]);

  if (!imageUrl) return null;

  // eslint-disable-next-line
  return <img src={imageUrl} style={style} onClick={onClick} alt="Added Image" />;
};

interface CameraMenuProps {
  info?: string;
  closeCamera: () => void;
  outputImages: (images: ImageUploadedProps[]) => void;
  inputImages?: ImageUploadedProps[];
  enableGallery?: boolean;
  selectedIndexInput?: number;
  limitImages?: number;
}

/**
 * Renders a camera menu component.
 * @param closeCamera - Function to close the camera menu.
 * @param outputImages - Function to output the captured images.
 * @param inputImages - Array of input images.
 * @param enableGallery - Flag to enable gallery.
 * @param selectedIndexInput - Index of the selected input image.
 */
export const CameraMenu = ({
  closeCamera,
  outputImages,
  inputImages,
  enableGallery,
  selectedIndexInput,
  limitImages = 5,
}: CameraMenuProps) => {
  const webcamRef = useRef(null);
  const [isCameraOpen, setIsCameraOpen] = useState(true);
  const [facingMode, setFacingMode] = useState(`environment`);
  const [img, setImg] = useState<ImageUploadedProps>({} as ImageUploadedProps);
  const [arrayImg, setArrayImg] = useState<ImageUploadedProps[]>(inputImages || []);
  const [selectedIndex, setSelectedIndex] = useState(selectedIndexInput || -1);
  const [showElement, setShowElement] = React.useState(false);

  /**
   * Timer for the limit message
   */
  useEffect(() => {
    const timer = setTimeout(function () {
      setShowElement(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, [arrayImg.length]);

  /**
   * Closes the camera and saves the images.
   */
  const closeAndSaveImages = () => {
    if (arrayImg.length > limitImages) {
      setShowElement(true);
    } else {
      outputImages(arrayImg);
      closeCamera();
    }
  };

  /**
   * Captures an image from the webcam and adds it to the array of images.
   * @returns {void}
   */
  const capture = useCallback(async () => {
    if (webcamRef.current !== null) {
      // const imageSrc = (webcamRef.current as Webcam).getScreenshot({ width: 1024, height: 1024 });
      const imageSrc = (webcamRef.current as Webcam).getScreenshot();
      if (imageSrc !== null) {
        const blob = new Blob([imageSrc], { type: `image/png` });
        const name = new Date().toString();
        setArrayImg((arrayImg) => [
          ...arrayImg,
          { tempId: generateRandomId(32), file_name: name, mime_type: `image/png`, data: blob },
        ]);
      }
    }
  }, [webcamRef]);

  /**
   * Toggles the facing mode of the camera.
   */
  const toggleFacingMode = useCallback(() => {
    setFacingMode((prevFacingMode) => (prevFacingMode === `user` ? `environment` : `user`));
  }, []);

  /**
   * The video constraints for the webcam.
   */
  const videoConstraints = {
    height: { ideal: 800, max: 800 },
    width: { ideal: 1000, max: 1000 },
    facingMode: facingMode,
  };

  // Create a file input to handle the image upload.
  const fileInput = document.createElement(`input`);
  fileInput.type = `file`;
  fileInput.accept = `image/jpeg, image/jpg, image/png`;
  fileInput.addEventListener(`change`, (event: Event) => {
    if (arrayImg.length >= limitImages) {
      setShowElement(true);
    } else {
      handleImageUpload(event as unknown as React.ChangeEvent<HTMLInputElement>);
    }
  });

  /**
   * Handles the image upload event.
   * @param event - The React change event.
   */
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const file = event.target.files[0];
      const reader = new FileReader();
      const fileName = file.name;
      reader.onload = async (e) => {
        const image = (e.target as FileReader).result;
        if (image) {
          fetch(String(image))
            .then((res) => res.blob())
            .then((blob) => {
              resizeImageIfNeeded(blob, 1000, 800).then((resizedBlob) => {
                blobToDataURL(resizedBlob)
                  .then((dataURL) => {
                    const finalBlob = new Blob([dataURL as string], { type: blob.type });
                    setArrayImg([
                      ...arrayImg,
                      {
                        tempId: generateRandomId(32),
                        file_name: fileName,
                        mime_type: blob.type as MimeTypes,
                        data: finalBlob,
                      },
                    ]);
                  })
                  .catch((err) => console.error(err));
              });
            })
            .catch((err) => console.error(err));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div
      style={{
        display: `flex`,
        justifyContent: `center`,
        alignItems: `center`,
        zIndex: 1004,
        height: `100%`,
        width: `100%`,
        backgroundColor: `black`,
        position: `absolute`,
        flexDirection: `column`,
        flexWrap: `nowrap`,
        overflowY: `auto`,
      }}
    >
      <Grid
        container
        style={{
          display: `flex`,
          justifyContent: `center`,
          alignItems: `center`,
          zIndex: 1004,
          flexDirection: `column`,
          flexWrap: `nowrap`,
          overflowY: `auto`,
          height: `100%`,
          width: `100%`,
        }}
      >
        {!img.data ? (
          <Webcam
            audio={false}
            mirrored={facingMode === `user`}
            forceScreenshotSourceSize
            ref={webcamRef}
            screenshotFormat="image/png"
            videoConstraints={videoConstraints}
            style={{ width: `100%`, height: `100%`, objectFit: `cover` }}
          />
        ) : (
          <ServiceImageCameraMenu
            imageData={img.data}
            style={{ height: `100%`, width: `100%`, objectFit: `contain` }}
          />
        )}
      </Grid>
      <Grid
        container
        style={{
          display: `flex`,
          height: `100%`,
          width: `100%`,
          justifyContent: `space-between`,
          zIndex: 1004,
          position: `absolute`,
          flexDirection: `column`,
          flexWrap: `nowrap`,
          overflowY: `auto`,
          padding: `17px 20px 17px 20px`,
        }}
      >
        <Grid
          item
          style={{
            height: `50px`,
            display: `flex`,
            flexDirection: `row`,
            justifyContent: `space-between`,
            alignItems: `center`,
          }}
        >
          {selectedIndex === -1 ? (
            <img
              src={iconTurnCamera}
              alt="Turn Camera"
              style={{ height: `50px`, width: `50px`, cursor: `pointer` }}
              onClick={toggleFacingMode}
            />
          ) : (
            <div style={{ height: `50px`, width: `50px`, cursor: `pointer` }}></div>
          )}

          <img
            src={iconCloseCamera}
            alt="Close Camera"
            style={{ height: `50px`, width: `50px`, cursor: `pointer` }}
            onClick={() => {
              if (isCameraOpen) {
                closeCamera();
              } else {
                setImg({} as ImageUploadedProps);
                setIsCameraOpen(true);
                setSelectedIndex(-1);
              }
            }}
          />
        </Grid>
        <Grid container item style={{ display: `flex`, flexDirection: `column` }}>
          {showElement && (
            <>
              <div
                style={{
                  backgroundColor: RoutalPalette.warning.medium,
                  borderRadius: `10px`,
                  display: `flex`,
                  justifyContent: `center`,
                  alignItems: `center`,
                  flexWrap: `nowrap`,
                  flexDirection: `row`,
                  alignSelf: `center`,
                  opacity: 0.9,
                  padding: `5px`,
                }}
              >
                <WarningAmberRoundedIcon style={{ color: RoutalPalette.secondary40, height: `30px`, width: `30px` }} />
                <Text
                  variant="body-large"
                  intlId={`${intl.formatMessage({
                    id: `driver.stop.camera.limit_message_start`,
                  })} ${limitImages} ${intl.formatMessage({ id: `driver.stop.camera.limit_message_end` })}`}
                  style={{ textAlign: `center`, padding: `2px`, color: RoutalPalette.secondary40 }}
                />
              </div>
              <VSpacer />
            </>
          )}
          <Grid
            item
            container
            style={{
              height: `100px`,
              display: `flex`,
              flexDirection: `row`,
              flexWrap: `nowrap`,
              justifyContent: `space-between`,
              marginBottom: `10px`,
            }}
          >
            <Grid
              item
              style={{
                display: `flex`,
                flexDirection: `row`,
                justifyContent: `flex-start`,
                overflowX: `auto`,
                overflowY: `hidden`,
                height: `110px`,
                width: `100%`,
                // padding: `0px 10px`,
              }}
            >
              <MaskedOverflow>
                {arrayImg.map((img, index) => (
                  <div
                    key={img.tempId}
                    style={{
                      position: `relative`,
                      border: selectedIndex === index ? `2px solid ${RoutalPalette.white}` : `none`,
                      marginRight: `5px`,
                      height: `100px`,
                      borderRadius: `5px`,
                    }}
                  >
                    <ServiceImageCameraMenu
                      imageData={img.data}
                      style={{
                        height: `100%`,
                        boxShadow: `rgba(0, 0, 0, 0.08) 0px 4px 12px`,
                        borderRadius: `5px`,
                        aspectRatio: `9/16`,
                        objectFit: `cover`,
                        filter: selectedIndex === index ? `brightness(0.5)` : `none`,
                      }}
                      onClick={() => {
                        if (index === selectedIndex) {
                          if (index !== -1) {
                            const updatedImages = [...arrayImg];
                            updatedImages.splice(index, 1);
                            setArrayImg(updatedImages);

                            // Set the image to the previous or next one if they exist
                            if (updatedImages.length > 0) {
                              const newIndex = index >= updatedImages.length ? 0 : index;
                              setImg(updatedImages[newIndex]);
                            } else {
                              setImg({} as ImageUploadedProps);
                              setIsCameraOpen(true);
                              setSelectedIndex(-1);
                            }
                          }
                        } else {
                          setImg(img);
                          setIsCameraOpen(false);
                          setSelectedIndex(index);
                        }
                      }}
                    />
                    {index === selectedIndex && (
                      <DeleteIcon
                        alt="Delete"
                        onClick={closeAndSaveImages}
                        style={{
                          pointerEvents: `none`,
                          position: `absolute`,
                          top: `52%`,
                          left: `51%`,
                          transform: `translate(-50%, -50%)`,
                          color: RoutalPalette.white,
                          fontSize: `30px`,
                          height: `35px`,
                          width: `35px`,
                          cursor: `pointer`,
                        }}
                      />
                    )}
                  </div>
                ))}
              </MaskedOverflow>
            </Grid>
            {!isCameraOpen ? (
              <Grid
                item
                className="btn-container"
                style={{ display: `flex`, justifyContent: `flex-end`, alignItems: `center` }}
              >
                <Grid style={{ position: `relative`, height: `50px` }}>
                  <img
                    src={iconUploadCamera}
                    alt="Upload"
                    style={{ height: `50px`, width: `50px`, cursor: `pointer` }}
                    onClick={closeAndSaveImages}
                  />
                  <div style={{ position: `absolute`, bottom: `4px`, right: `11px`, color: RoutalPalette.white }}>
                    {arrayImg.length}
                  </div>
                </Grid>
              </Grid>
            ) : null}
          </Grid>

          <Grid
            item
            style={{
              display: `flex`,
              flexDirection: `row`,
              justifyContent: `space-between`,
              alignItems: `center`,
            }}
          >
            {isCameraOpen ? (
              <>
                <div style={{ height: `50px`, width: `50px`, cursor: `pointer` }}>
                  <img
                    src={iconGallery}
                    alt="Open Gallery"
                    style={{
                      height: `50px`,
                      width: `50px`,
                      cursor: `pointer`,
                      pointerEvents: !enableGallery ? `none` : undefined,
                      opacity: !enableGallery ? `0` : undefined,
                    }}
                    onClick={() => {
                      fileInput.click();
                    }}
                  />
                </div>

                <div
                  style={{
                    borderRadius: `100%`,
                    border: `3px solid ${RoutalPalette.white}`,
                  }}
                  onClick={() => {
                    if (arrayImg.length >= limitImages) {
                      setShowElement(true);
                    } else {
                      capture();
                    }
                  }}
                >
                  <div
                    style={{
                      backgroundColor: RoutalPalette.white,
                      borderRadius: `100%`,
                      height: `80px`,
                      width: `80px`,
                      margin: `4px`,
                    }}
                  ></div>
                </div>
                <Grid
                  style={{
                    opacity: arrayImg.length > 0 || (inputImages && inputImages?.length > 0) ? 1 : 0,
                    pointerEvents: arrayImg.length > 0 || (inputImages && inputImages?.length > 0) ? undefined : `none`,
                    position: `relative`,
                    height: `50px`,
                  }}
                >
                  <img
                    src={iconUploadCamera}
                    alt="Upload"
                    style={{ height: `50px`, width: `50px`, cursor: `pointer` }}
                    onClick={closeAndSaveImages}
                  />
                  {arrayImg.length > 0 && (
                    <div
                      style={{
                        position: `absolute`,
                        bottom: `6px`,
                        right: `12px`,
                        color: RoutalPalette.white,
                        userSelect: `none`,
                        pointerEvents: `none`,
                      }}
                    >
                      {arrayImg.length}
                    </div>
                  )}
                </Grid>
              </>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};
